import { Color, Flex, Icon, Link, Space, Spacing, Switch, Text, Tooltip } from '@billy/design-system'
import { type ChangeEvent, type ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useComponentId, useFormContext } from '../../../../../../../../../../../../hooks'
import { LegalSettingTypes } from '../../../../../../../../enums/legalSettingTypes'
import type { ComplianceSettingsFormSchema } from '../../../../../../../../types/complianceSettingsFormSchema'

interface LegalSettingSwitchProps {
  legalSettingType: LegalSettingTypes
  onLearnMoreClick: () => void
}

export const LegalSettingSwitch = ({ legalSettingType, onLearnMoreClick }: LegalSettingSwitchProps): ReactElement => {
  const { t } = useTranslation()
  const { setValue, watch } = useFormContext<ComplianceSettingsFormSchema>()
  const titleId = useComponentId('legal-setting-switch-title')

  const isChecked = watch(`legalFormSettings.${legalSettingType}`)

  const isLegalGuaranteeOfConformity = legalSettingType === LegalSettingTypes.IsLegalGuaranteeOfConformity

  const { annotation, description, learnMore, title } = useMemo(() => {
    switch (legalSettingType) {
      case LegalSettingTypes.IsLegalGuaranteeOfConformity:
        return {
          annotation: t(
            'settings.organization.external_invoice.legal_mentions.legal_guarantee_of_conformity.annotation',
          ),
          description: t(
            'settings.organization.external_invoice.legal_mentions.legal_guarantee_of_conformity.description',
          ),
          learnMore: t(
            'settings.organization.external_invoice.legal_mentions.legal_guarantee_of_conformity.learn_more',
          ),
          title: t('settings.organization.external_invoice.legal_mentions.legal_guarantee_of_conformity.title'),
        }
      case LegalSettingTypes.IsMemberOfAccreditedAssociation:
        return {
          description: t(
            'settings.organization.external_invoice.legal_mentions.member_of_accredited_association.description',
          ),
          title: t('settings.organization.external_invoice.legal_mentions.member_of_accredited_association.title'),
        }
      case LegalSettingTypes.IsVatOnDebitsEnabled:
        return {
          description: t('settings.organization.external_invoice.legal_mentions.vat_on_debits.description'),
          title: t('settings.organization.external_invoice.legal_mentions.vat_on_debits.title'),
        }
    }
  }, [legalSettingType, t])

  const handleSwitchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setValue(`legalFormSettings.${legalSettingType}`, event.target.checked, { shouldDirty: true })
    },
    [legalSettingType, setValue],
  )

  const handleLearnMoreClick = useCallback(() => {
    onLearnMoreClick()
  }, [onLearnMoreClick])

  return (
    <Flex alignItems="flex-start" gap={Spacing.S8} justifyContent="space-between">
      <Flex.Item>
        <Text as="span" colorVariant="primary" id={titleId} weight="bold">
          {title}
        </Text>{' '}
        {isLegalGuaranteeOfConformity ? (
          <>
            <Space size="s4" />
            <Flex alignItems="flex-start" flexDirection="column" gap={Spacing.S8}>
              <Text colorVariant="secondary">{description}</Text>
              <Link onClick={handleLearnMoreClick} underlined={false}>
                <Text as="span" color={Color.Lavender500} weight="bold">
                  {learnMore}
                </Text>
              </Link>
              {isChecked && (
                <Text colorVariant="secondary" variant="bodySM">
                  {annotation}
                </Text>
              )}
            </Flex>
          </>
        ) : (
          <Tooltip as="span" label={description} placement="top">
            <Icon icon="info" size="sm" />
          </Tooltip>
        )}
      </Flex.Item>

      <Switch aria-labelledby={titleId} checked={isChecked} onChange={handleSwitchChange} />
    </Flex>
  )
}
